import React from "react";
import Modal from "../utils/Modal/Modal";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { ReactComponent as CheckboxUncheckedIcon } from "../../assets/icons/checkbox-unchecked.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../assets/icons/checkbox-checked.svg";
import { ReactComponent as LockIcon } from "../../assets/icons/lock.svg";
import { ReactComponent as StarIcon } from "../../assets/icons/whiteStar.svg";

import {
  CheckboxContainer,
  LockContainer,
  ModalContainer,
  PromptCancelButton,
  PromptCloseButton,
  PromptInputContainer,
  PromptInputTitle,
  PromptModalButtons,
  PromptModalHeader,
  PromptModalTitle,
  PromptSeedInput,
  PromptSubmitButton,
  PromptTextarea,
  ProOnly,
  VideoQualityCheckboxContainer,
  VideoQualityContainer,
  VideoQualityDescription,
  VideoQualityItem,
  VideoQualityText,
  VideoQualityTitle,
  FpsContainer,
  FpsTitle,
  FpsSlider,
  FpsLabels,
  FpsLabel,
} from "./EnterPromptModal.styles";
import {
  ALL_VIDEO_RESOLUTIONS,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/user/slice";

const VIDEO_RESOLUTION_DESCRIPTION = {
  LOW: "1-2 minutes",
  MEDIUM: "3-4 minutes",
  HIGH: "5-6 minutes",
};

const FPS_OPTIONS = [30, 45, 60];

const PRO_PLAN_NAME = "Pro";
const PREMIUM_PLAN_NAME = "Premium";

const snapToClosestFps = (value) => {
  const numValue = Number(value);

  if (numValue < 37.5) return 30;

  if (numValue < 52.5) return 45;

  return 60;
};

const EnterPromptModal = ({
  isOpen,
  onClose,
  customPrompt,
  setCustomPrompt,
  videoResolution,
  setVideoResolution,
  setEditedResolution,
  collectionVideoResolution,
  enableVideoQuality,
  modelPrice,
  regeneratePrice,
  isEdited,
  customSeed,
  setCustomSeed,
  fps,
  setFps,
  setEditedVideoFps,
  collectionVideoFps,
}) => {
  const user = useSelector(selectUser);
  const [tempFps, setTempFps] = React.useState(fps || DEFAULT_VIDEO_FPS);

  React.useEffect(() => {
    setTempFps(fps || DEFAULT_VIDEO_FPS);
  }, [fps]);

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  const handleFpsSnap = (value) => {
    const snappedValue = snapToClosestFps(value);
    setFps(snappedValue);
    setTempFps(snappedValue);
    localStorage.setItem("videoFps", snappedValue);

    setEditedVideoFps(
      snappedValue !== (Number(collectionVideoFps) ?? DEFAULT_VIDEO_FPS)
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContainer>
        <PromptModalHeader>
          <PromptModalTitle>Customize Scene</PromptModalTitle>
          <PromptCloseButton
            onClick={() => {
              onClose();
              setCustomPrompt("");
            }}
          >
            <CloseIcon />
          </PromptCloseButton>
        </PromptModalHeader>

        <PromptInputContainer>
          <PromptInputTitle>Custom Prompt</PromptInputTitle>
          <PromptTextarea
            placeholder="Enter your custom prompt here..."
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
          />
        </PromptInputContainer>

        <PromptInputContainer>
          <PromptInputTitle>Seed</PromptInputTitle>
          <PromptSeedInput
            placeholder="Enter your seed here..."
            value={customSeed}
            min={1}
            max={10000000}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (value >= 1 && value <= 10000000) {
                setCustomSeed(value);
              }
            }}
          />
        </PromptInputContainer>

        <VideoQualityContainer>
          {enableVideoQuality && (
            <>
              <VideoQualityTitle>Video Quality</VideoQualityTitle>
              {ALL_VIDEO_RESOLUTIONS.map((videoResolutionType) => (
                <VideoQualityItem key={videoResolutionType}>
                  <VideoQualityCheckboxContainer
                    $isDisabled={
                      videoResolutionType === "HIGH" && !isProEnabled
                    }
                    onClick={() => {
                      setVideoResolution(videoResolutionType);

                      setEditedResolution(
                        videoResolutionType !==
                          (collectionVideoResolution ??
                            DEFAULT_VIDEO_RESOLUTION)
                      );

                      localStorage.setItem(
                        "videoResolution",
                        videoResolutionType
                      );
                    }}
                  >
                    <CheckboxContainer
                      $checked={videoResolutionType === videoResolution}
                    >
                      {videoResolutionType === videoResolution ? (
                        <CheckboxCheckedIcon color="#4D9DFF" />
                      ) : (
                        <CheckboxUncheckedIcon color="#4D9DFF" />
                      )}
                    </CheckboxContainer>

                    <VideoQualityText>
                      {videoResolutionType.charAt(0).toUpperCase() +
                        videoResolutionType.slice(1).toLowerCase()}
                    </VideoQualityText>

                    <StarIcon
                      width={16}
                      height={16}
                      style={{ marginRight: "-4px" }}
                    />
                    <VideoQualityText>
                      {collectionVideoResolution === videoResolutionType &&
                      !isEdited
                        ? Math.ceil(regeneratePrice)
                        : modelPrice +
                          VIDEO_RESOLUTION_PRICE[videoResolutionType]}
                    </VideoQualityText>
                  </VideoQualityCheckboxContainer>

                  {videoResolutionType !== "HIGH" || isProEnabled ? (
                    <VideoQualityDescription>
                      {VIDEO_RESOLUTION_DESCRIPTION[videoResolutionType]}
                    </VideoQualityDescription>
                  ) : (
                    <LockContainer>
                      <LockIcon width={16} height={16}></LockIcon>
                      <ProOnly>Pro only</ProOnly>
                    </LockContainer>
                  )}
                </VideoQualityItem>
              ))}
            </>
          )}
        </VideoQualityContainer>

        <FpsContainer>
          <FpsTitle>FPS</FpsTitle>

          <FpsSlider
            type="range"
            min={30}
            max={60}
            step={1}
            value={tempFps}
            onInput={(e) => {
              setTempFps(Number(e.target.value));
            }}
            onChange={(e) => {
              setTempFps(Number(e.target.value));
            }}
            onMouseUp={(e) => {
              handleFpsSnap(Number(e.target.value));
            }}
            onTouchEnd={() => {
              handleFpsSnap(tempFps);
            }}
          />

          <FpsLabels>
            {FPS_OPTIONS.map((fpsValue) => (
              <FpsLabel key={fpsValue}>{fpsValue}FPS</FpsLabel>
            ))}
          </FpsLabels>
        </FpsContainer>

        <PromptModalButtons>
          <PromptCancelButton
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </PromptCancelButton>
          <PromptSubmitButton
            onClick={() => {
              onClose(false);
            }}
            disabled={false}
          >
            Confirm
          </PromptSubmitButton>
        </PromptModalButtons>
      </ModalContainer>
    </Modal>
  );
};

export default EnterPromptModal;
